import { ArticleJsonLdProps, BreadCrumbJsonLdProps, NextSeoProps } from 'next-seo';

import pageRoutes from '../../router/pageRoutes';
import { StrapiApiArticleAttributes, StrapiApiTag } from '../integrations/strapi/types';
import { OrganizationLogo } from './organizationSeoConfig';
import { toAbsoluteUrl } from './urls';

// Strapi articles carry no author field, so posts are attributed to the company entity.
// This replaces the literal "N/A" that was previously published in the structured data.
const PublisherName = 'Gaspar AI';

// Builds the canonical, fully qualified URL for a blog post.
const getBlogPostUrl = (pageUrl: string) => toAbsoluteUrl(`${pageRoutes.Blog}/${pageUrl}`);

export const getBlogPostJsonSeoConfig = (attributes: StrapiApiArticleAttributes): ArticleJsonLdProps => {
  const { title, seo_description: description, imageUrl, publishedAt, updatedAt, page_url } = attributes;
  return {
    useAppDir: false,
    type: 'BlogPosting',
    isAccessibleForFree: true,
    url: getBlogPostUrl(page_url),
    title,
    description,
    images: [imageUrl],
    datePublished: String(publishedAt),
    // Falls back to publishedAt only when Strapi has no updatedAt, so the freshness signal is real.
    dateModified: String(updatedAt || publishedAt),
    authorName: { type: 'Organization', name: PublisherName, url: toAbsoluteUrl('/') },
    publisherName: PublisherName,
    publisherLogo: OrganizationLogo.url,
  };
};

// Breadcrumbs let search and answer engines place a post within the site hierarchy
// instead of treating it as an orphaned page.
export const getBlogPostBreadcrumbSeoConfig = (attributes: StrapiApiArticleAttributes): BreadCrumbJsonLdProps => {
  const { title, page_url } = attributes;
  return {
    itemListElements: [
      { position: 1, name: 'Home', item: toAbsoluteUrl('/') },
      { position: 2, name: 'Blog', item: toAbsoluteUrl(pageRoutes.Blog) },
      { position: 3, name: title, item: getBlogPostUrl(page_url) },
    ],
  };
};

export const getBlogPostPageSeoConfig = (attributes: StrapiApiArticleAttributes, tags?: StrapiApiTag[]): NextSeoProps => {
  const { title, seo_description: description, imageUrl, imageDescription, publishedAt, updatedAt, page_url } = attributes;
  const url = getBlogPostUrl(page_url);
  const articleSeo = {
    publishedTime: String(publishedAt),
    modifiedTime: String(updatedAt || publishedAt),
    authors: [toAbsoluteUrl('/')],
    tags: (tags || []).map((tag) => tag.attributes.name),
  };
  const images = [{ url: imageUrl, alt: imageDescription, type: 'image/jpeg' }];
  // The canonical is set per post. Without it the site wide default pointed every post at the homepage.
  return { title, description, canonical: url, openGraph: { url, title, type: 'article', description, images, article: articleSeo } };
};
